import { executeGraphQlQuery } from 'helpers/storefront-graphql-helper'
import { useEffect, useState } from 'preact/hooks'
import Pagination from 'snippets/pagination/pagination'
import BlogCard from 'snippets/blog-card/blog-card'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'

const selectors = {
  PAGE_SIZE: 9,
  PAGE_SIZE_MOBILE: 8
}

function BlogAll() {
  const blogHandles = window.blogHandles
  const [currentPage, setCurrentPage] = useState(1)
  const [totalBlogItems, setTotalBlogItems] = useState([])
  const [currentBlogItems, setCurrentBlogItems] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const { isTablet } = useDetectBreakpoint()

  useEffect(async () => {
    setIsFetching(true)
    const fetchBlogs = async () => {
      const blogData = blogHandles.map(async (handle) => {
        const param = `query {
          blog(handle: "${handle}") {
            handle
            id
            title
            articles(first: 250) {
              nodes {
                handle
                blog {
                  handle
                  title
                }
                id
                image {
                  id
                  height
                  altText
                  url
                  width
                }
                tags
                title
                contentHtml
                onlineStoreUrl
                content(truncateAt: 100)
                publishedAt
              }
            }
          }
        }`
        const response = await executeGraphQlQuery({ query: param })
          .then((data) => data.json())
          .then((data) => {
            if (data.errors) {
              throw data
            }
            return data.data
          })
          .catch((error) => {
            console.warn('error', error)
            return {
              results: []
            }
          })

        return response?.blog?.articles || { nodes: [] }
      })

      const blogs = await Promise.all(blogData)
      return blogs.reduce((acc, curr) => {
        return [...acc, ...curr.nodes]
      }, [])
    }
    const blogItems = await fetchBlogs()
    const sortBlogItemsHidden = blogItems.filter((item) => !item.tags.find((x) => x === 'hidden') && item)
    const sortBlogItems = sortBlogItemsHidden.sort(function (a, b) {
      return new Date(b.publishedAt) - new Date(a.publishedAt)
    })
    setCurrentBlogItems(sortBlogItems)
    const items = getCurrentData(currentPage, sortBlogItems)
    setTotalBlogItems(items)
    setIsFetching(false)
  }, [])

  const getCurrentData = (currentPage, items) => {
    const tempData = [...items]
    const firstPageIndex = (currentPage - 1) * (isTablet ? selectors.PAGE_SIZE_MOBILE : selectors.PAGE_SIZE)
    const lastPageIndex = firstPageIndex + (isTablet ? selectors.PAGE_SIZE_MOBILE : selectors.PAGE_SIZE)
    return tempData.slice(firstPageIndex, lastPageIndex)
  }

  const handlePageChange = (page) => {
    document.scrollingElement.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    setCurrentPage(page)
  }

  useEffect(() => {
    const sortBlogItems = currentBlogItems.sort(function (a, b) {
      return new Date(b.publishedAt) - new Date(a.publishedAt)
    })
    const items = getCurrentData(currentPage, sortBlogItems)
    setTotalBlogItems(items)
  }, [currentPage])

  const isHidden = (tags) => tags.find((x) => x === 'hidden')

  return isFetching ? (
    <PlpGridOriginSkeleton />
  ) : (
    <>
      {totalBlogItems.length > 0 ? (
        <div className="px-4 py-8 lg:px-12 lg:pb-[72px] lg:pt-8">
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-8 md:gap-y-12 lg:grid-cols-3">
            {totalBlogItems.map(
              (item) =>
                !isHidden(item.tags) && (
                  <BlogCard
                    id={item.id}
                    imageUrl={item.image?.url}
                    altText={item.image?.altText}
                    categoryTitle={item.blog?.title}
                    title={item.title}
                    onlineStoreUrl={item.onlineStoreUrl}
                    content={item.content}
                    buttonText="Read more"
                  />
                )
            )}
          </div>
          <Pagination
            currentPage={currentPage}
            totalCount={currentBlogItems.length}
            pageSize={isTablet ? selectors.PAGE_SIZE : selectors.PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </div>
      ) : (
        <div className="px-4 py-8 lg:px-12 lg:pb-[72px] lg:pt-8">
          <h2 class="h5 lg:h4 flex justify-center font-semibold">No blogs</h2>
        </div>
      )}
    </>
  )
}

function PlpGridOriginSkeleton() {
  return (
    <div className="px-4 py-8 lg:px-12 lg:pb-[72px] lg:pt-8">
      <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-8 md:gap-y-12 lg:grid-cols-3">
        {[...Array(9)].map(() => (
          <div className="flex flex-col gap-y-6 overflow-hidden p-0 md:w-full md-max:border-r-0">
            <div className="relative aspect-[344/196] w-full overflow-hidden lg:aspect-[426/260]">
              <div className="aspect-square relative w-full bg-[linear-gradient(270deg,#F9FAFB_18.75%,#E5E7EB_179.69%)] pb-[100%]"></div>
            </div>
            <div class="flex flex-auto flex-col">
              <div class="h-5 w-full bg-[linear-gradient(270deg,#F9FAFB_18.75%,#E5E7EB_179.69%)] text-sm font-medium text-grey-500"></div>
              <h3 class="h5 mb-3 h-8 w-full bg-[linear-gradient(270deg,#F9FAFB_18.75%,#E5E7EB_179.69%)] font-medium"></h3>
              <p class="mb-auto h-8 w-full bg-[linear-gradient(270deg,#F9FAFB_18.75%,#E5E7EB_179.69%)] text-base"></p>
              <button
                type="button"
                class="mt-6 h-5 bg-[linear-gradient(270deg,#F9FAFB_18.75%,#E5E7EB_179.69%)] text-left text-base font-semibold text-primary underline underline-offset-4 hover:text-primary-hover"
              ></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default BlogAll
